import { FILTER_SECTIONS } from '@/app/library/constants';
import { formatDatetime } from '@/utils/formatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AspectRatio {
  /** Display label including descriptor, e.g. "1:1 (Square)" */
  label: string;
  /** Numeric-only ratio string, e.g. "1:1" */
  value: string;
}

export type Language = (typeof LANGUAGES)[number];

// ---------------------------------------------------------------------------
// Aspect Ratios — single-select list
// ---------------------------------------------------------------------------

export const ASPECT_RATIOS: readonly AspectRatio[] = Object.freeze([
  { label: '1:1 (Square)', value: '1:1' },
  { label: '4:5 (Portrait)', value: '4:5' },
  { label: '3:4 (Portrait)', value: '3:4' },
  { label: '2:3 (Portrait)', value: '2:3' },
  { label: '9:16 (Vertical)', value: '9:16' },
  { label: '5:4 (Landscape)', value: '5:4' },
  { label: '4:3 (Landscape)', value: '4:3' },
  { label: '3:2 (Landscape)', value: '3:2' },
  { label: '16:9 (Landscape)', value: '16:9' },
  { label: '21:9 (Ultrawide)', value: '21:9' },
]);

// ---------------------------------------------------------------------------
// Constellation Library — curated MediaFilterPanel filter set (Creative persona)
// ---------------------------------------------------------------------------

export const CONSTELLATION_MEDIA_FILTERS: readonly string[] = Object.freeze([
  FILTER_SECTIONS.CONTENT_TAGS,
  FILTER_SECTIONS.PREDICTED_PERFORMANCE,
  FILTER_SECTIONS.SOURCES,
  FILTER_SECTIONS.MEDIA_TYPES,
  FILTER_SECTIONS.DATE_ADDED,
  FILTER_SECTIONS.SIMILAR_IMAGES,
  FILTER_SECTIONS.ASPECT_RATIO,
  FILTER_SECTIONS.COLOR,
  FILTER_SECTIONS.DUPLICATE_MEDIA,
  FILTER_SECTIONS.MEDIA_SIZE,
]);

// ---------------------------------------------------------------------------
// Languages — single-select list (13 languages per prod spec)
// ---------------------------------------------------------------------------

export const LANGUAGES = [
  'Arabic',
  'Catalan',
  'Dutch',
  'English',
  'French',
  'German',
  'Hindi',
  'Italian',
  'Japanese',
  'Polish',
  'Portuguese',
  'Russian',
  'Spanish',
] as const;

// ---------------------------------------------------------------------------
// Variations — 1 through 10
// ---------------------------------------------------------------------------

export const VARIATIONS_RANGE: readonly string[] = Object.freeze(
  Array.from({ length: 10 }, (_, i) => String(i + 1)),
);

// ---------------------------------------------------------------------------
// Auto-name helper
// ---------------------------------------------------------------------------

export const AUTO_NAME_PREFIX = 'Project';

/**
 * Generate a default project name using user-local readable datetime.
 * Format: "Project - May 20, 2026, 10:42:30 AM"
 */
export function formatAutoProjectName(now: Date = new Date()): string {
  return `${AUTO_NAME_PREFIX} - ${formatDatetime(now, 'MMM D, YYYY, h:mm:ss A')}`;
}

// ---------------------------------------------------------------------------
// Prompt-as-description helper
// ---------------------------------------------------------------------------

const PROMPT_DESCRIPTION_HEADER = 'Initial Prompt';
const PROMPT_DESCRIPTION_DIVIDER = '--------------';

/**
 * Format a (possibly multi-line) prompt into a gallery description, e.g.
 *
 * ```
 * Initial Prompt
 * --------------
 * Here is an example prompt where the user is saying what they want to see.
 * --------------
 * ```
 *
 * Returns an empty string for a blank prompt so we don't persist an empty shell.
 */
export function formatPromptDescription(prompt: string): string {
  const trimmed = prompt.trim();
  if (!trimmed) return '';
  return [
    PROMPT_DESCRIPTION_HEADER,
    PROMPT_DESCRIPTION_DIVIDER,
    trimmed,
    PROMPT_DESCRIPTION_DIVIDER,
  ].join('\n');
}
